import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  `
    inline-flex items-center justify-center
    gap-2 whitespace-nowrap rounded-lg font-bold
    font-medium transition-all disabled:pointer-events-none
    disabled:bg-button-disabled [&_svg]:pointer-events-none
    [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0
    outline-none focus-visible:border-ring focus-visible:ring-ring/50
    focus-visible:ring-[3px] aria-invalid:ring-destructive/20 
    dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive
    hover:cursor-pointer disabled:cursor-not-allowed
  `,
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: `
          border border-input-border bg-background shadow-xs 
          hover:bg-accent hover:text-accent-foreground 
          dark:bg-input/30 dark:border-input dark:hover:bg-input/50
          disabled:bg-input-backdrop disabled:border-input-border
          disabled:text-label-text
        `,
      secondary:
        "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
      ghost: "",
      link: "text-primary underline-offset-4 hover:underline",
      },
      color: {
        success: `
          bg-success text-white hover:bg-success hover:opacity-90
          disabled:bg-success    
        `,
      },
      size: {
        default: "h-11 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 font-semibold text-sm rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className,
  variant,
  size,
  color,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className, color }))}
      type="button"
      {...props}
    />
  )
}

export { Button, buttonVariants }
